<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import type { DropdownMenuProps } from './dropdown-menu';
import { ref } from 'vue';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';

const props = defineProps<DropdownMenuProps>();

const menuButtonRef = ref<HTMLElement | null>(null);
const menuItemsRef = ref<HTMLElement | null>(null);
const { floatingStyles } = useFloating(menuButtonRef, menuItemsRef, {
  placement: props.placement,
  whileElementsMounted: autoUpdate,
  strategy: 'fixed',
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  middleware: [offset(4), flip(), shift({ padding: 8 })],
});
</script>

<template>
  <Menu v-slot="{ open }">
    <MenuButton ref="menuButtonRef" as="div">
      <slot />
    </MenuButton>
    <Teleport v-if="open" to="body">
      <MenuItems
        ref="menuItemsRef"
        :style="floatingStyles"
        class="z-1 flex max-h-[260px] w-80 origin-top-right flex-col gap-2 overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-2 shadow-2xl focus-visible:outline-none"
      >
        <MenuItem
          v-for="item in props.items"
          :key="item.label"
          v-slot="{ active }"
          as="button"
        >
          <button
            :class="[
              'flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold transition',
              active
                ? 'bg-primary-50 text-primary-600'
                : 'text-neutral-700 hover:bg-neutral-50',
            ]"
            @click="item.onClick?.()"
          >
            <HugeIcon v-if="item.icon" :icon="item.icon" />
            {{ item.label }}
          </button>
        </MenuItem>
      </MenuItems>
    </Teleport>
  </Menu>
</template>
